import React from 'react';

import { Mutation } from '@apollo/client/react/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  styled,
} from '@mui/material';
import Uppy from '@uppy/core';
import { Dashboard } from '@uppy/react';
import dayjs from 'dayjs';

import QueryWrapper from '../../../../components/QueryWrapper/QueryWrapper';
import Toast from '../../../../components/Toast/Toast';
import auth from '../../../../services/Auth';
import contactGQLSchemas from '../../../../services/contacts/gqlSchema';
import documentSchema from '../../../../services/documents/gqlSchema';
import uploadSchema from '../../../../services/uploads/gqlSchema';
import UppyGraphQl from '../../../../services/uppy/graphql';

import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';

// eslint-disable-next-line new-cap
const uppyRib = Uppy({
  meta: { type: 'avatar' },
  restrictions: { maxNumberOfFiles: 1 },
  autoProceed: true,
});
uppyRib.use(UppyGraphQl, {});

// eslint-disable-next-line new-cap
const uppyId = Uppy({
  meta: { type: 'avatar' },
  restrictions: { maxNumberOfFiles: 1 },
  autoProceed: true,
});
uppyId.use(UppyGraphQl, {});

// eslint-disable-next-line new-cap
const uppyRSAC = Uppy({
  meta: { type: 'avatar' },
  restrictions: { maxNumberOfFiles: 1 },
  autoProceed: true,
});
uppyRSAC.use(UppyGraphQl, {});

// eslint-disable-next-line new-cap
const uppyRSACProof = Uppy({
  meta: { type: 'avatar' },
  restrictions: { maxNumberOfFiles: 1 },
  autoProceed: true,
});
uppyRSACProof.use(UppyGraphQl, {});

// eslint-disable-next-line new-cap
const uppyAddressProof = Uppy({
  meta: { type: 'avatar' },
  restrictions: { maxNumberOfFiles: 1 },
  autoProceed: true,
});
uppyAddressProof.use(UppyGraphQl, {});

const isMinRequiredDocuments = data => {
  const requiredDocs = [
    { id: 'ID', count: 0 },
    { id: 'RIB', count: 0 },
    { id: 'DOMICILE', count: 0 },
  ];

  if (!data || data.contactInfosCandidateDocuments.length < 3) return false;

  const documents = data.contactInfosCandidateDocuments;
  for (let i = 0; i < documents.length; i++) {
    const currentDoc = documents[i].category || '';
    for (let j = 0; j < requiredDocs.length; j++) {
      const requiredDoc = requiredDocs[j];
      if (currentDoc === requiredDoc.id) {
        requiredDoc.count++;
        break;
      }
    }
  }

  for (let j = 0; j < requiredDocs.length; j++) {
    if (requiredDocs[j].count === 0) return false;
  }

  return true;
};

const CustomBox = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  position: 'relative',
  width: '100%',
  padding: '8px 48px 8px 16px',
});

const CandidateCardDocuments = ({ tabInfos }) => {
  const currentContact = auth.getContact();
  const contactInfo = currentContact.contactsInfos.find(
    infos => infos.sources && infos.sources.source === 'SALESFORCE',
  );
  const contactInfoId = contactInfo.id;

  const displayCategoryLabel = category => {
    switch (category) {
      case 'ID':
        return "Pièce d'identité";
      case 'RIB':
        return 'RIB';
      case 'PROFIL':
        return "Photo d'identité";
      case 'RSAC':
        return 'RSAC définitif';
      case 'RSAC_PROOF':
        return 'Preuve de dépôt RSAC';
      case 'DOMICILE':
        return 'Justificatif de domicile';
      default:
        return '';
    }
  };

  const onClickDeleteDocument = async (id, deleteContactDocument) => {
    if (window.confirm('Êtes-vous sûr(e) de vouloir supprimer cette photo ?\nCette action est irréversible.')) {
      await deleteContactDocument({ variables: { id } });
    }
  };

  return (
    <Box>
      <FontAwesomeIcon icon={tabInfos.icon} />
      <Box component='span' sx={{ marginLeft: '5px', fontSize: '1rem', fontWeight: '700', textTransform: 'uppercase' }}>
        {tabInfos.title}
      </Box>

      <Box sx={{ paddingTop: '30px' }}>
        <QueryWrapper query={documentSchema.Query.contactInfosCandidateDocuments} variables={{ contactInfoId }}>
          {({ data }) => {
            if (isMinRequiredDocuments(data)) {
              return (
                <Alert severity='success'>
                  Merci! Les documents seront très prochainement vérifiés par le service recrutement.
                </Alert>
              );
            } else {
              return (
                <Alert severity='warning'>
                  Veuillez fournir au minimum: une pièce d'identité, un RIB et un justificatif de domicile
                </Alert>
              );
            }
          }}
        </QueryWrapper>

        <Mutation
          mutation={uploadSchema.Mutation.uploadContactInfosDocuments}
          refetchQueries={[
            {
              query: documentSchema.Query.contactInfosCandidateDocuments,
              variables: { contactInfoId },
            },
            {
              query: contactGQLSchemas.Query.contact,
              variables: { id: currentContact.id },
            },
          ]}
        >
          {(uploadContactInfosDocuments, uploadContactInfosDocumentsStatus) => {
            uppyRib.getPlugin('GraphQl').setOptions({
              schema: uploadContactInfosDocuments,
              variables: {
                contactInfoId: contactInfoId,
                location: 'DOCUMENTS',
                category: 'RIB',
                uniqueInCategory: false,
              },
            });
            uppyRib.on('complete', result => {
              uppyRib.reset();
            });

            uppyId.getPlugin('GraphQl').setOptions({
              schema: uploadContactInfosDocuments,
              variables: {
                contactInfoId: contactInfoId,
                location: 'DOCUMENTS',
                category: 'ID',
                uniqueInCategory: false,
              },
            });
            uppyId.on('complete', result => {
              uppyId.reset();
            });

            uppyRSACProof.getPlugin('GraphQl').setOptions({
              schema: uploadContactInfosDocuments,
              variables: {
                contactInfoId: contactInfoId,
                location: 'DOCUMENTS',
                category: 'RSAC_PROOF',
                uniqueInCategory: false,
              },
            });
            uppyRSACProof.on('complete', result => {
              uppyRSACProof.reset();
            });

            uppyRSAC.getPlugin('GraphQl').setOptions({
              schema: uploadContactInfosDocuments,
              variables: {
                contactInfoId: contactInfoId,
                location: 'DOCUMENTS',
                category: 'RSAC',
                uniqueInCategory: false,
              },
            });
            uppyRSAC.on('complete', result => {
              uppyRSAC.reset();
            });

            uppyAddressProof.getPlugin('GraphQl').setOptions({
              schema: uploadContactInfosDocuments,
              variables: {
                contactInfoId: contactInfoId,
                location: 'DOCUMENTS',
                category: 'DOMICILE',
                uniqueInCategory: false,
              },
            });
            uppyAddressProof.on('complete', result => {
              uppyAddressProof.reset();
            });

            return (
              <>
                {uploadContactInfosDocumentsStatus.error ? (
                  <Toast
                    error={
                      "Une erreur est survenue lors de l'envois de votre fichier. Merci de ré-essayer ulterieurement."
                    }
                  />
                ) : null}
                <Grid container spacing={3} className='drop-zones' sx={{ marginTop: '15px' }}>
                  <Grid item xs={4}>
                    <Dashboard
                      uppy={uppyId}
                      inline
                      height={200}
                      locale={{
                        strings: {
                          dropPaste: "Déposez une pièce d'identité ici ou %{browse}",
                          browse: 'naviguer',
                        },
                      }}
                      proudlyDisplayPoweredByUppy={false}
                      hideUploadButton
                      hideRetryButton
                      hidePauseResumeButton
                      hideCancelButton
                      hideProgressAfterFinish
                      showProgressDetails
                      showSelectedFiles={false}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Dashboard
                      uppy={uppyRib}
                      inline
                      height={200}
                      locale={{
                        strings: {
                          dropPaste: 'Déposez votre RIB ici ou %{browse}',
                          browse: 'naviguer',
                        },
                      }}
                      proudlyDisplayPoweredByUppy={false}
                      hideUploadButton
                      hideRetryButton
                      hidePauseResumeButton
                      hideCancelButton
                      hideProgressAfterFinish
                      showProgressDetails
                      showSelectedFiles={false}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Dashboard
                      uppy={uppyRSACProof}
                      inline
                      height={200}
                      locale={{
                        strings: {
                          dropPaste: 'Déposez votre preuve de dépôt RSAC ici ou %{browse}',
                          browse: 'naviguer',
                        },
                      }}
                      proudlyDisplayPoweredByUppy={false}
                      hideUploadButton
                      hideRetryButton
                      hidePauseResumeButton
                      hideCancelButton
                      hideProgressAfterFinish
                      showProgressDetails
                      showSelectedFiles={false}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Dashboard
                      uppy={uppyRSAC}
                      inline
                      height={200}
                      locale={{
                        strings: {
                          dropPaste: 'Déposez votre RSAC définitif ici ou %{browse}',
                          browse: 'naviguer',
                        },
                      }}
                      proudlyDisplayPoweredByUppy={false}
                      hideUploadButton
                      hideRetryButton
                      hidePauseResumeButton
                      hideCancelButton
                      hideProgressAfterFinish
                      showProgressDetails
                      showSelectedFiles={false}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Dashboard
                      uppy={uppyAddressProof}
                      inline
                      height={200}
                      locale={{
                        strings: {
                          dropPaste: 'Déposez votre Justificatif de domicile ici ou %{browse}',
                          browse: 'naviguer',
                        },
                      }}
                      proudlyDisplayPoweredByUppy={false}
                      hideUploadButton
                      hideRetryButton
                      hidePauseResumeButton
                      hideCancelButton
                      hideProgressAfterFinish
                      showProgressDetails
                      showSelectedFiles={false}
                    />
                  </Grid>
                </Grid>
                <List>
                  <QueryWrapper
                    query={documentSchema.Query.contactInfosCandidateDocuments}
                    variables={{ contactInfoId: contactInfoId }}
                  >
                    {({ data }) => {
                      return data.contactInfosCandidateDocuments.map(doc => {
                        return (
                          <Box key={doc.id}>
                            <ListItem key={doc.id} sx={{ padding: '0', alignItems: 'flex-start' }}>
                              <CustomBox>
                                <ListItemButton
                                  component='a'
                                  href={doc.public_url}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                >
                                  <ListItemText
                                    primary={displayCategoryLabel(doc.category)}
                                    secondary={
                                      <>
                                        {doc.filename}
                                        <Typography
                                          component='span'
                                          sx={{ display: 'block', fontSize: '13px', marginTop: '5px' }}
                                        >
                                          Publié le {dayjs(doc.createdAt).format('DD/MM/YYYY à HH:mm:ss')}
                                        </Typography>
                                      </>
                                    }
                                  ></ListItemText>
                                </ListItemButton>
                                <Mutation
                                  mutation={uploadSchema.Mutation.deleteContactDocument}
                                  refetchQueries={[
                                    {
                                      query: documentSchema.Query.contactInfosCandidateDocuments,
                                      variables: { contactInfoId },
                                    },
                                    {
                                      query: contactGQLSchemas.Query.contact,
                                      variables: { id: currentContact.id },
                                    },
                                  ]}
                                >
                                  {(deleteContactDocument, { loading, error }) => {
                                    return (
                                      <ListItemSecondaryAction>
                                        <IconButton
                                          edge='end'
                                          aria-label='delete'
                                          onClick={() => onClickDeleteDocument(doc.assoc_id, deleteContactDocument)}
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </ListItemSecondaryAction>
                                    );
                                  }}
                                </Mutation>
                              </CustomBox>
                            </ListItem>
                            <Divider />
                          </Box>
                        );
                      });
                    }}
                  </QueryWrapper>
                </List>
              </>
            );
          }}
        </Mutation>
      </Box>
    </Box>
  );
};

export default React.memo(CandidateCardDocuments);
